import React from 'react';
import { SortType } from './types';

type ToolbarProps = {
  filter: string;
  setFilter: (filter: string) => void;
  sort: SortType;
  setSort: (sort: SortType) => void;
  origin: string[];
  setOrigin: (origin: string[]) => void;
  onOpenStock: () => void;
};

const SearchIcon = () => (
  <svg
    className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400'
    fill='none'
    stroke='currentColor'
    viewBox='0 0 24 24'
  >
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth={2}
      d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
    />
  </svg>
);

const StockIcon = () => (
  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth={2}
      d='M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
    />
  </svg>
);

const SortArrow = ({ isDescending }: { isDescending: boolean }) => (
  <svg
    className={`w-3 h-3 ${isDescending ? 'rotate-180' : ''}`}
    fill='none'
    stroke='currentColor'
    viewBox='0 0 24 24'
  >
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth={2}
      d='M5 15l7-7 7 7'
    />
  </svg>
);

const SortButton = ({
  label,
  isActive,
  isDescending,
  onClick,
}: {
  label: string;
  isActive: boolean;
  isDescending: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? 'bg-turquoise text-white'
        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
    }`}
  >
    <span>{label}</span>
    {isActive && <SortArrow isDescending={isDescending} />}
  </button>
);

const OriginButton = ({
  label,
  isActive,
  onClick,
}: {
  label: string;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
      isActive
        ? 'bg-turquoise text-white'
        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
    }`}
  >
    {label}
  </button>
);

export const Toolbar = ({
  filter,
  setFilter,
  sort,
  setSort,
  origin,
  setOrigin,
  onOpenStock,
}: ToolbarProps) => {
  const toggleOrigin = (value: string) => {
    if (origin.includes(value)) {
      setOrigin(origin.filter((el) => el !== value));
    } else {
      setOrigin([...origin, value]);
    }
  };

  return (
    <div className='px-5 py-3 bg-gray-50 border-b border-gray-100'>
      {/* Recherche et bouton stock */}
      <div className='flex items-center gap-3'>
        <div className='flex-1 relative'>
          <SearchIcon />
          <input
            type='text'
            placeholder='Rechercher un granit...'
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className='w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-turquoise/30 focus:border-turquoise transition-all'
          />
        </div>

        <button
          onClick={onOpenStock}
          className='flex items-center gap-2 px-3 py-2 text-sm font-medium text-turquoise-dark bg-turquoise-light hover:bg-turquoise/20 rounded-lg transition-colors'
        >
          <StockIcon />
          <span>Stock</span>
        </button>
      </div>

      {/* Filtres secondaires */}
      <div className='flex items-center gap-4 mt-3'>
        {/* Tri */}
        <div className='flex items-center gap-2'>
          <span className='text-xs text-gray-500'>Trier:</span>
          <SortButton
            label='Nom'
            isActive={sort.includes('name')}
            isDescending={sort === 'descending_name'}
            onClick={() =>
              setSort(
                sort === 'ascending_name'
                  ? 'descending_name'
                  : 'ascending_name',
              )
            }
          />
          <SortButton
            label='Prix'
            isActive={sort.includes('price')}
            isDescending={sort === 'descending_price'}
            onClick={() =>
              setSort(
                sort === 'ascending_price'
                  ? 'descending_price'
                  : 'ascending_price',
              )
            }
          />
        </div>

        {/* Origine */}
        <div className='flex items-center gap-2'>
          <span className='text-xs text-gray-500'>Origine:</span>
          <OriginButton
            label='Français'
            isActive={origin.includes('FRENCH')}
            onClick={() => toggleOrigin('FRENCH')}
          />
          <OriginButton
            label='Import'
            isActive={origin.includes('IMPORT')}
            onClick={() => toggleOrigin('IMPORT')}
          />
        </div>
      </div>
    </div>
  );
};
